/* eslint-disable react-hooks/static-components */
'use client'
import { FC, memo } from 'react'

import { useAtomValue } from 'jotai'

import { getVehicleResourceKey, mapRoute } from '@dfds-pax/utils'

import { Skeleton } from '@/app/[locale]/[ferry]/Skeleton'
import { getVehicleIcon, isFootPassenger } from '@/app/lib/vehicleHelpers'
import { PassengerCategory } from '@/components/summary/interfaces'
import { selectedPetsAtom, selectedRoutesAtom } from '@/states/selectionAtom'
import ArrowBackIcon from '@/ui-cvi/components/dxp-icons/system/ArrowBackIcon'
import ArrowForwardIcon from '@/ui-cvi/components/dxp-icons/system/ArrowForwardIcon'
import ArrowReverseIcon from '@/ui-cvi/components/dxp-icons/system/ArrowReverseIcon'
import FerryReturnIcon from '@/ui-cvi/components/dxp-icons/system/FerryReturnIcon'
import PassengerIcon from '@/ui-cvi/components/dxp-icons/system/PassengerIcon'

import { useTranslation } from '@/hooks/useDebugTranslations'
import type { VehicleDimensionsState } from '@/hooks/useVehicleDimensions'

const getContentfulString = (resourceKey: string, count: number) => {
  if (resourceKey === 'Adult') return count === 1 ? 'ADULT-WITH-PLACEHOLDER' : 'ADULT-WITH-PLACEHOLDER-PLURAL'
  if (resourceKey === 'Child') return count === 1 ? 'CHILD-WITH-PLACEHOLDER' : 'CHILD-WITH-PLACEHOLDER-PLURAL'
  if (resourceKey === 'Infant') return count === 1 ? 'INFANT-WITH-PLACEHOLDER' : 'INFANT-WITH-PLACEHOLDER-PLURAL'
  if (resourceKey === 'Pet') {
    if (count === 0 && window.location.pathname.includes('pet')) return 'PET-CHOOSE'
    if (count > 0 && !window.location.pathname.includes('yourbooking'))
      return count === 1 ? 'PET-WITH-PLACEHOLDER' : 'PET-WITH-PLACEHOLDER-PLURAL'
  }
  return ''
}

const Vehicle = ({
  height,
  length,
  quantity,
  vehicleCode,
}: {
  height?: number
  length?: number
  quantity: number
  vehicleCode: string
}) => {
  if (isFootPassenger(vehicleCode)) return null
  const Icon = getVehicleIcon(vehicleCode)
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { t } = useTranslation()

  return (
    <div className="gap-primitive-8 lg:gap-semantic-global-md flex flex-row items-center">
      {Icon ? <Icon className="text-dfds-text-dark-primary size-6 lg:size-5" /> : null}
      <div
        className="typography-body-md-regular text-semantic-global-text-default flex flex-row flex-wrap gap-x-2 text-left"
        data-e2e="booking-summary-vehicle"
      >
        <span>{`${quantity} x ${t(getVehicleResourceKey(vehicleCode))}`}</span>
        {height !== undefined && length !== undefined ? <span>{`<${height}m x <${length}m`}</span> : null}
      </div>
    </div>
  )
}

const Trailer = ({
  height,
  length,
  quantity,
  trailerCode,
}: {
  height?: number
  length?: number
  quantity?: number
  trailerCode: string
}) => {
  const Icon = getVehicleIcon(trailerCode)
  const { t } = useTranslation()

  return (
    <div className="gap-primitive-8 lg:gap-semantic-global-md text-semantic-global-text-default flex flex-row items-center">
      {Icon ? <Icon className="text-dfds-text-dark-primary gap-primitive-8 lg:gap-semantic-global-md size-6 lg:size-5" /> : null}
      <div
        className="typography-body-md-regular text-semantic-global-text-default flex flex-row flex-wrap gap-x-2 text-left"
        data-e2e="booking-summary-trailer"
      >
        <span>{`${quantity} x ${t(getVehicleResourceKey(trailerCode))}`}</span>
        {height !== undefined && length !== undefined ? <span>{`<${height}m x <${length}m`}</span> : null}
      </div>
    </div>
  )
}

// Reserves the vehicle/trailer line while its dimensions load via useVehicleDimensions.
const VehicleLineSkeleton = () => (
  <div className="gap-primitive-8 lg:gap-semantic-global-md flex flex-row items-center" data-e2e="booking-summary-vehicle-skeleton">
    <Skeleton className="size-6 lg:size-5" square />
    <Skeleton className="h-4 w-40" />
  </div>
)

const Passengers = ({ categories, petCount }: { categories: PassengerCategory[]; petCount?: number }) => {
  const { t } = useTranslation()

  const hasChildren = categories?.filter((c) => c?.type === 'Child')?.length > 0

  const selectedPets = useAtomValue(selectedPetsAtom)
  const numberOfPets =
    petCount ?? (selectedPets.showNumberOfPets > 0 ? selectedPets.pets?.map((p) => p.selectedQuantity).reduce((a, b) => a + b, 0) : 0)

  return (
    <div className="i gap-primitive-8 lg:gap-semantic-global-md flex flex-row">
      <PassengerIcon className="text-dfds-text-dark-primary size-6 lg:size-5" />
      <div className="flex flex-col justify-start text-start" data-e2e="booking-summary-passenger">
        {categories.map((c, i) => {
          return (
            <div key={i}>
              {c?.type === 'Adult' ? (
                <div className="typography-body-md-regular text-semantic-global-text-default">
                  {t(getContentfulString(c.type, c.quantity), { count: c.quantity })}
                </div>
              ) : null}
              {c?.type === 'Child' ? (
                <div className="typography-body-md-regular text-semantic-global-text-default">
                  {t(getContentfulString(c.type, c.quantity), { count: c.quantity })}
                  <span className="pl-primitive-4">
                    (
                    {c.ages?.map((age, index) => (
                      <span className="mr-primitive-4" key={index}>
                        {age}
                        {c.ages && index !== c.ages.length - 1 ? ',' : ''}
                      </span>
                    ))}
                    {t('YEARS')})
                  </span>
                </div>
              ) : null}
              {c?.type === 'Infant' && hasChildren ? (
                <div className="typography-body-md-regular text-semantic-global-text-default">
                  {t(getContentfulString(c.type, c.quantity), { count: c.quantity })}
                  <span className="pl-primitive-4">
                    (
                    {c.ages?.map((age, index) => (
                      <span className="mr-primitive-4" key={index}>
                        {age}
                        {c.ages && index !== c.ages.length - 1 ? ',' : ''}
                      </span>
                    ))}
                    {t('YEARS')})
                  </span>
                </div>
              ) : null}

              {c?.type === 'Infant' && !hasChildren ? (
                <div className="typography-body-md-regular text-semantic-global-text-default">
                  {t(getContentfulString(c.type, c.quantity), { count: c.quantity })}
                  <span className="pl-primitive-4">
                    (
                    {c.ages?.map((age, index) => (
                      <span className="mr-primitive-4" key={index}>
                        {age}
                        {c.ages && index !== c.ages.length - 1 ? ',' : ''}
                      </span>
                    ))}
                    {t('YEARS')})
                  </span>
                </div>
              ) : null}

              {i === categories.length - 1 && (petCount === undefined ? selectedPets.showNumberOfPets > 0 : petCount > 0) ? (
                <div className="typography-body-md-regular text-semantic-global-text-default">
                  {t(getContentfulString('Pet', numberOfPets), { count: numberOfPets })}
                </div>
              ) : null}
            </div>
          )
        })}
      </div>
    </div>
  )
}

interface Props {
  categories: PassengerCategory[]
  petCount?: number
  routes?: { outbound: string; homebound: string | null }
  trailerCode?: string
  trailerDimensions?: VehicleDimensionsState
  vehicleCode?: string
  vehicleDimensions?: VehicleDimensionsState
}
const TravelInfo: FC<Props> = ({ categories, petCount, routes, trailerCode, trailerDimensions, vehicleCode, vehicleDimensions }) => {
  const storedRoutes = useAtomValue(selectedRoutesAtom)
  const selectedRoutes = routes ?? storedRoutes
  const outboundUnloRoute =
    selectedRoutes?.outbound.trim() && selectedRoutes.outbound.length === 4 ? mapRoute(selectedRoutes.outbound) : undefined
  const homeboundUnLoRoute =
    selectedRoutes?.homebound?.trim() && selectedRoutes.homebound.length === 4 ? mapRoute(selectedRoutes.homebound) : undefined
  const outboundAndHomeboundRoutesAreTheSame = homeboundUnLoRoute
    ? outboundUnloRoute?.departureUnLoCode === homeboundUnLoRoute.arrivalUnLoCode &&
      outboundUnloRoute?.arrivalUnLoCode === homeboundUnLoRoute.departureUnLoCode
    : false

  const { t } = useTranslation()

  return (
    <div className="gap-primitive-8 lg:gap-semantic-global-sm flex flex-col items-start">
      <div className="gap-primitive-8 lg:gap-semantic-global-sm flex flex-row items-center">
        <FerryReturnIcon className="text-dfds-text-dark-primary size-6 lg:size-5" />
        {outboundUnloRoute ? (
          outboundAndHomeboundRoutesAreTheSame ? (
            <div className="flex flex-row items-center">
              <span className="typography-body-md-regular text-semantic-global-text-default" data-e2e="booking-summary-outbound-departure">
                {t(`${outboundUnloRoute.departureUnLoCode}`)}
              </span>
              <span className="mx-primitive-8">
                <ArrowReverseIcon size="md" />
              </span>
              <span className="typography-body-md-regular text-semantic-global-text-default" data-e2e="booking-summary-return-departure">
                {t(`${outboundUnloRoute.arrivalUnLoCode}`)}
              </span>
            </div>
          ) : (
            <div className="flex flex-col">
              <div className="flex flex-row items-center">
                <span
                  className="typography-body-md-regular text-semantic-global-text-default"
                  data-e2e="booking-summary-outbound-departure"
                >
                  {t(`${outboundUnloRoute.departureUnLoCode}`)}
                </span>
                <span className="mx-primitive-8">
                  <ArrowForwardIcon size="md" />
                </span>
                <span className="typography-body-md-regular text-semantic-global-text-default" data-e2e="booking-summary-return-departure">
                  {t(`${outboundUnloRoute.arrivalUnLoCode}`)}
                </span>
              </div>
              {homeboundUnLoRoute ? (
                <div className="flex flex-row items-center">
                  <span className="typography-body-md-regular text-semantic-global-text-default">
                    {t(`${homeboundUnLoRoute.arrivalUnLoCode}`)}
                  </span>
                  <span className="mx-primitive-8">
                    <ArrowBackIcon size="md" />
                  </span>
                  <span className="typography-body-md-regular text-semantic-global-text-default">
                    {t(`${homeboundUnLoRoute.departureUnLoCode}`)}
                  </span>
                </div>
              ) : null}
            </div>
          )
        ) : null}
      </div>

      {vehicleCode && !isFootPassenger(vehicleCode) ? (
        vehicleDimensions?.loading ? (
          <VehicleLineSkeleton />
        ) : (
          <Vehicle
            height={vehicleDimensions?.dimensions?.height}
            length={vehicleDimensions?.dimensions?.length}
            quantity={1}
            vehicleCode={vehicleCode}
          />
        )
      ) : null}
      {trailerCode ? (
        trailerDimensions?.loading ? (
          <VehicleLineSkeleton />
        ) : (
          <Trailer
            height={trailerDimensions?.dimensions?.height}
            length={trailerDimensions?.dimensions?.length}
            quantity={1}
            trailerCode={trailerCode}
          />
        )
      ) : null}
      <Passengers categories={categories} petCount={petCount} />
    </div>
  )
}

export default memo(TravelInfo)
